import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  Avatar,
  Skeleton,
  Menu,
  Tooltip,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import { useSelector } from "react-redux";
import CandidateTimelinePanel from "bundles/Internal/components/candidate/CandidateTimelinePanel";
import CandidateJobApplicationsPanel from "bundles/Internal/components/candidate/CandidateJobApplicationsPanel";
import {
  ArrowLeft,
  MoreVertical,
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  GraduationCap,
  Sparkles,
  IdCard,
  Globe,
  Workflow,
  Megaphone,
  Settings,
  ScanText,
  AlertTriangle,
  FileText,
  Download,
  MousePointerClick,
} from "lucide-react";
import translations from "app/libs/i18n/translations.json";
import FeatureActivityPanel from "bundles/Internal/components/common/FeatureActivityPanel";
import { enqueueSnackbar } from "notistack";
import { useIntl } from "react-intl";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import AddCandidateToPipelineFlow from "bundles/Internal/components/common/AddCandidateToPipelineFlow";
import ChangePasswordDialog from "bundles/Internal/components/common/ChangePasswordDialog";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import CvViewer from "bundles/Internal/components/common/CvViewer";
import { formatRegionMessage, humanizeRegion } from "bundles/common/utils/utils";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";
import FollowButton from "bundles/Internal/components/common/FollowButton";
import { GenerateCvSplitButton } from "bundles/Internal/components/common/BrandedCvMenu";
import {
  brandedErrorCopy,
  brandedStageDetail,
  brandedStageLabel,
  brandedStageProgress,
} from "bundles/Internal/components/cv_vault/BrandedCvStatusPanel";

const COLUMNS_CONSTANTS = [
  { key: "id", label: "Id" },
  { key: "godview_url", label: "Godview" },
  { key: "first_name", label: "First name" },
  { key: "last_name", label: "Last name" },
  { key: "region", label: "Region" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "resume_url", label: "Resume" },
  { key: "city", label: "City" },
  { key: "zipcode", label: "Zipcode" },
  { key: "job_applications_count", label: "Job applications count" },
  { key: "profile_completion", label: "Profile completion" },
  { key: "created_at", label: "Created at" },
  { key: "updated_at", label: "Updated at" },
  { key: "signup_origin", label: "Signup origin" },
  { key: "availability", label: "Availability" },
  { key: "work_permit", label: "Work permit" },
  { key: "desired_sector", label: "Desired sector" },
  { key: "experience_year", label: "Experience year" },
  { key: "candidate_status", label: "Candidate status" },
  { key: "country", label: "Country" },
  { key: "whitelisted", label: "Whitelisted" },
  { key: "blacklist_reasons", label: "Blacklist reasons" },
  { key: "current_stage_name", label: "Current stage" },
  { key: "job_title", label: "Job title" },
  { key: "desired_job_title", label: "Desired job title" },
  { key: "desired_job_type", label: "Desired job type" },
  { key: "desired_locations", label: "Desired locations" },
  { key: "highest_degree", label: "Highest degree" },
  { key: "utm_source", label: "Utm source" },
  { key: "utm_campaign", label: "Utm campaign" },
  { key: "utm_medium", label: "Utm medium" },
  { key: "current_sign_in_at", label: "Current sign in at" },
  { key: "deleted_at", label: "Deleted at" },
  { key: "last_sign_in_at", label: "Last sign in at" },
  { key: "portfolio_url", label: "Portfolio url" },
  { key: "preferred_locale", label: "Preferred locale" },
  { key: "presentation", label: "Presentation" },
  { key: "recruiter_summaries", label: "AI summary" },
  { key: "sign_in_count", label: "Sign in count" },
  { key: "candidate_interests_count", label: "Candidate interests count" },
  { key: "blacklisted_company_ids", label: "Blacklisted company ids" },
  { key: "password_generated", label: "Password generated" },
];

const URL_FIELDS = {
  godview_url: "Godview",
  resume_url: "Resume",
  portfolio_url: "Portfolio",
};

const formatCandidateCell = (candidate, key) => {
  const value = candidate?.[key];
  if (value === null || value === undefined) return "";

  if (key === "region" && value) {
    return formatRegionMessage(value);
  }

  // Per-locale cache: pick the candidate's own language, then whatever else is there. A recruiter
  // reading this on the show page wants the AI's synthesized paragraph, not a locale->text dump.
  if (key === "recruiter_summaries" && value && typeof value === "object") {
    const locale = value[candidate.preferred_locale] ? candidate.preferred_locale : Object.keys(value)[0];
    return locale ? value[locale] : "";
  }

  if (URL_FIELDS[key] && value) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#079344", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
      >
        {URL_FIELDS[key]} ↗
      </a>
    );
  }

  if (key === "desired_locations" && Array.isArray(value)) {
    return value.map((l) => (typeof l === "string" ? l : l?.label)).filter(Boolean).join(", ");
  }

  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const KNOWN_SOURCES = ["forem", "vdab", "actiris", "france_travail", "stepstone", "apec", "indeed", "linkedin"];

const DETAIL_SECTIONS = [
  { title: "Contact", icon: IdCard, keys: ["first_name", "last_name", "email", "phone", "preferred_locale"] },
  { title: "Location", icon: MapPin, keys: ["region", "city", "zipcode", "country"] },
  {
    title: "Professional profile",
    icon: Briefcase,
    keys: [
      "job_title", "experience_year", "desired_job_title", "desired_job_type", "desired_sector",
      "desired_locations", "candidate_status", "availability", "highest_degree", "work_permit",
      "portfolio_url", "presentation", "recruiter_summaries",
    ],
  },
  {
    title: "Pipeline & status",
    icon: Workflow,
    keys: [
      "current_stage_name", "profile_completion", "job_applications_count", "candidate_interests_count",
      "whitelisted", "blacklist_reasons", "blacklisted_company_ids",
    ],
  },
  { title: "Source & tracking", icon: Megaphone, keys: ["signup_origin", "utm_source", "utm_medium", "utm_campaign"] },
  {
    title: "Account",
    icon: Settings,
    keys: [
      "id", "godview_url", "resume_url", "created_at", "updated_at", "current_sign_in_at",
      "last_sign_in_at", "sign_in_count", "password_generated", "deleted_at",
    ],
  },
];

// Anything in COLUMNS_CONSTANTS that no section claims still gets rendered, so adding a column
// to the table can't make it silently vanish from this page.
const GROUPED_KEYS = new Set(DETAIL_SECTIONS.flatMap((s) => s.keys));
const UNGROUPED_KEYS = COLUMNS_CONSTANTS.map((c) => c.key).filter((k) => !GROUPED_KEYS.has(k));

const SKILL_CATEGORY_LABELS = {
  hard: "Hard skills",
  software: "Software",
  soft: "Soft skills",
  lang: "Languages",
  licence: "Licences",
};

const skillLabel = (skill) =>
  translations.en?.[`skills_list.${skill.category}.${skill.name}`] || skill.name;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatMonth = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return `${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const formatDateRange = (start, end) => {
  const from = formatMonth(start);
  if (!from) return "";
  return `${from} — ${formatMonth(end) || "Present"}`;
};

const sectionTitleSx = {
  color: "hsl(150 30% 12%)",
  fontWeight: 600,
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 2,
};

const SectionTitle = ({ icon: Icon, children, count }) => (
  <Typography sx={sectionTitleSx}>
    <Icon size={18} color="hsl(150 38% 17%)" />
    {children}
    {count > 0 && (
      <Box component="span" sx={{ color: "hsl(150 10% 46%)", fontWeight: 400 }}>
        ({count})
      </Box>
    )}
  </Typography>
);

const DetailTile = ({ label, children }) => (
  <Box sx={{ p: 2, borderRadius: "calc(0.75rem - 2px)", backgroundColor: "hsl(140 10% 95%)", height: "100%" }}>
    <Typography
      sx={{
        color: "hsl(150 10% 46%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 600,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: "hsl(150 30% 12%)",
        mt: 0.5,
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
        display: "flex",
        alignItems: "center",
        gap: 0.5,
      }}
    >
      {children}
    </Box>
  </Box>
);

const RecordCard = ({ title, subtitle, meta, chip, children }) => (
  <Box
    sx={{
      position: "relative",
      pl: 2.5,
      py: 1.75,
      pr: 2,
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      "&:before": {
        content: "\"\"",
        position: "absolute",
        left: 0,
        top: 12,
        bottom: 12,
        width: 3,
        borderRadius: 3,
        backgroundColor: "hsl(150 38% 17%)",
      },
    }}
  >
    <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: "hsl(150 38% 17%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
            {subtitle}
          </Typography>
        )}
        {meta && (
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)", mt: 0.25, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
            {meta}
          </Typography>
        )}
      </Box>
      {chip && (
        <Chip
          label={chip}
          size="small"
          sx={{
            flexShrink: 0,
            fontSize: 11,
            height: 22,
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            fontFamily: "Segoe UI Regular, sans-serif, system-ui",
          }}
        />
      )}
    </Stack>
    {children}
  </Box>
);

const EmptySection = ({ children }) => (
  <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
    {children}
  </Typography>
);

const PARSE_STATUS_TONE = {
  applied: { label: "Applied to the profile", color: "hsl(150 38% 17%)", bg: "hsl(140 30% 94%)" },
  parsed: { label: "Parsed", color: "hsl(150 38% 17%)", bg: "hsl(140 30% 94%)" },
  review: { label: "Needs review", color: "hsl(35 92% 30%)", bg: "hsl(40 100% 94%)" },
  failed: { label: "Failed", color: "hsl(0 72% 45%)", bg: "hsl(0 80% 96%)" },
  pending: { label: "In progress", color: "hsl(150 10% 40%)", bg: "hsl(140 10% 94%)" },
};

// Until now nothing on this page said a profile had been filled in by a model, on what confidence,
// or what the parser proposed that Rails then refused.
const CvParsePanel = ({ receipt }) => {
  if (!receipt) return null;

  const tone = PARSE_STATUS_TONE[receipt.status] || PARSE_STATUS_TONE.pending;
  const lowConfidence = receipt.low_confidence_fields || [];
  const dropped = receipt.dropped || [];

  return (
    <Box>
      <SectionTitle icon={ScanText}>CV extraction</SectionTitle>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        <Chip
          size="small"
          label={tone.label}
          sx={{ fontSize: 12, fontWeight: 600, color: tone.color, backgroundColor: tone.bg, border: `1px solid ${tone.color}33` }}
        />
        {receipt.model_version && (
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>{receipt.model_version}</Typography>
        )}
        {receipt.detected_language && (
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
            CV language: {receipt.detected_language.toUpperCase()}
          </Typography>
        )}
        {receipt.created_at && (
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
            {new Date(receipt.created_at).toLocaleDateString()}
          </Typography>
        )}
      </Stack>

      {receipt.status === "failed" && receipt.error && (
        <Box sx={{ p: 1.25, mb: 1.5, borderRadius: "8px", backgroundColor: "hsl(0 80% 97%)", border: "1px solid hsl(0 60% 88%)" }}>
          <Typography sx={{ fontSize: 12, color: "hsl(0 50% 38%)" }}>{receipt.error}</Typography>
        </Box>
      )}

      {lowConfidence.length > 0 && (
        <Box sx={{ p: 1.25, mb: 1.5, borderRadius: "8px", backgroundColor: "hsl(40 100% 95%)", border: "1px solid hsl(40 80% 82%)" }}>
          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <AlertTriangle size={16} color="hsl(35 92% 33%)" style={{ marginTop: "1px", flexShrink: 0 }} />
            <Box>
              <Typography sx={{ fontSize: 12, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(35 92% 30%)" }}>
                {lowConfidence.length} field{lowConfidence.length === 1 ? " was" : "s were"} below the confidence
                threshold ({Math.round((receipt.confidence_threshold || 0.85) * 100)}%) and {lowConfidence.length === 1 ? "was" : "were"} not applied
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                {lowConfidence.map((field) => (
                  <Typography key={field.field} sx={{ fontSize: 12, color: "hsl(35 60% 30%)" }}>
                    <strong>{humanizeRegion(field.field)}</strong>
                    {field.value ? `: ${Array.isArray(field.value) ? field.value.join(", ") : field.value}` : ""}
                    {` (${Math.round(field.confidence * 100)}%)`}
                    {field.snippet ? `. “${field.snippet}”` : ""}
                  </Typography>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}

      {dropped.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          {dropped.map((group) => (
            <Typography key={group.label} sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
              Not saved. {group.label}: {group.values.join(", ")}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

// The candidate's own resume never carries a Squarehub CV status field, so it is fetched
// separately (like CreateCandidateModal/VaultDetail do) rather than piggy-backed on the show payload.
const BrandedCvSection = ({ candidateId, hasCv, branded, onGenerate }) => {
  if (!hasCv) return null;

  return (
    <Box>
      <SectionTitle icon={FileText}>Squarehub CV</SectionTitle>

      {branded?.status === "generating" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={14} sx={{ color: "hsl(150 38% 17%)" }} />
            <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)" }}>
              {brandedStageLabel(branded?.stage)}
              {brandedStageDetail(branded?.stage, branded?.progress) ? ` · ${brandedStageDetail(branded?.stage, branded?.progress)}` : ""}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={brandedStageProgress(branded?.stage)}
            sx={{
              width: 220,
              height: 2,
              borderRadius: 2,
              backgroundColor: "hsl(140 8% 90%)",
              "& .MuiLinearProgress-bar": { backgroundColor: "hsl(150 38% 17%)" },
            }}
          />
        </Box>
      ) : branded?.status === "failed" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Typography sx={{ fontSize: 12.5, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(0 55% 40%)" }}>
            {brandedErrorCopy({ errorKind: branded?.progress?.error_kind, error: branded?.error }).title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
            {brandedErrorCopy({ errorKind: branded?.progress?.error_kind, error: branded?.error }).detail}
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <GenerateCvSplitButton onRun={onGenerate} label="Regenerate" />
          </Box>
        </Box>
      ) : branded?.status === "ready" ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            size="small"
            component="a"
            href={`/internal/candidates/${candidateId}/branded_cv/download`}
            download={branded.filename}
            startIcon={<Download size={15} />}
            sx={{ textTransform: "none", fontSize: 12, minHeight: 0, py: 0.25, borderRadius: "8px", color: "hsl(150 38% 17%)", border: "1px solid hsl(140 8% 82%)", px: 1.5 }}
          >
            Download PDF
          </Button>
          <Box sx={{ flex: 1 }} />
          <GenerateCvSplitButton onRun={onGenerate} label="Regenerate" />
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", gap: 1 }}>
          <Typography sx={{ fontSize: 12.5, color: "hsl(150 10% 46%)" }}>No Squarehub CV yet for this candidate</Typography>
          <Box sx={{ flex: 1 }} />
          <GenerateCvSplitButton onRun={onGenerate} />
        </Stack>
      )}
    </Box>
  );
};

// Mirrors VaultDetail's ViewerTabs pill toggle so a recruiter who already knows the CV Vault
// finds the same document/text/branded switch here.
const CvDocTabs = ({ value, onChange, officialLabel, hasOfficial, hasText, alternateLabel, hasAlternate }) => (
  <Box sx={{ display: "flex", flexShrink: 0, p: "2px", gap: "2px", borderRadius: "8px", backgroundColor: "hsl(140 8% 92%)" }}>
    {[
      { key: "official", label: officialLabel, enabled: hasOfficial },
      { key: "text", label: "Extracted text", enabled: hasText },
      { key: "alternate", label: alternateLabel, enabled: hasAlternate },
    ].map((tab) => (
      <Box
        key={tab.key}
        component="button"
        type="button"
        disabled={!tab.enabled}
        onClick={() => tab.enabled && onChange(tab.key)}
        sx={{
          all: "unset",
          px: 1.25,
          height: 26,
          display: "flex",
          alignItems: "center",
          borderRadius: "6px",
          fontSize: 12,
          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
          cursor: tab.enabled ? "pointer" : "default",
          opacity: tab.enabled ? 1 : 0.4,
          color: value === tab.key ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
          backgroundColor: value === tab.key ? "#ffffff" : "transparent",
          boxShadow: value === tab.key ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
        }}
      >
        {tab.label}
      </Box>
    ))}
  </Box>
);

const TAB_KEYS = ["details", "job_applications", "cv", "activity"];

function CandidateShowPage() {
  const navigate = useNavigate();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const { id } = useParams();
  const intl = useIntl();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [candidate, setCandidate] = useState(location.state?.candidate || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingWhitelisted, setSavingWhitelisted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [whitelistConfirmValue, setWhitelistConfirmValue] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const activeTab = Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0);
  const setActiveTab = (v) => setSearchParams({ tab: TAB_KEYS[v] }, { replace: true, state: location.state });
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/candidates"));
  const [addToPipelineOpen, setAddToPipelineOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  const [jobAppCount, setJobAppCount] = useState(location.state?.candidate?.live_job_applications_count ?? null);
  const { openEditCandidate } = useCandidateModal();
  const [branded, setBranded] = useState(null);

  const [cvDocTab, setCvDocTab] = useState("official");

  const actionsOpen = Boolean(actionsAnchorEl);
  const isWhitelisted = Boolean(candidate?.whitelisted);
  const isBlacklisted = !isWhitelisted;
  const isArchived = Boolean(candidate?.deleted_at);
  const hasCv = Boolean(candidate?.resume_url || candidate?.cv_text);

  const fetchCandidate = useCallback(() => {
    if (!id) return;

    sh.get(`/internal/candidates/${id}`)
      .then((res) => {
        setCandidate(res.data);
        setJobAppCount(res.data.live_job_applications_count ?? null);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to load candidate");
      });
  }, [id]);

  const fetchBranded = useCallback(() => {
    if (!id) return;
    sh.get(`/internal/candidates/${id}/branded_cv`)
      .then((res) => setBranded(res.data?.status ? res.data : null))
      .catch(() => setBranded(null));
  }, [id]);

  const generateBrandedCv = useCallback((locale) => {
    if (!id) return;
    sh.post(`/internal/candidates/${id}/branded_cv`, locale ? { overrides: { locale } } : {})
      .then(() => {
        setBranded((prev) => ({ ...(prev || {}), status: "generating", error: null }));
        enqueueSnackbar("Writing the Squarehub CV", { variant: "success" });
      })
      .catch(() => enqueueSnackbar("Could not start the Squarehub CV", { variant: "error" }));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (candidate && !candidate.live_job_applications_count && candidate.live_job_applications_count !== 0) {
      setLoading(false);
    }

    fetchCandidate();
    fetchBranded();
  }, [id]);

  // No ActionCable channel reaches an internal recruiter scoped to one candidate's branded CV,
  // so a generation in progress is tracked by polling instead.
  useEffect(() => {
    if (branded?.status !== "generating") return undefined;
    const timer = setInterval(fetchBranded, 3000);
    return () => clearInterval(timer);
  }, [branded?.status, fetchBranded]);

  const title = useMemo(() => {
    if (!candidate) return "";
    const name = [candidate.first_name, candidate.last_name].filter(Boolean).join(" ");
    return name || candidate.email || `Candidate #${candidate.id}`;
  }, [candidate]);

  const getInitials = () => {
    if (!candidate) return "?";
    const first = candidate.first_name?.[0] || "";
    const last = candidate.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  const detailColumns = useMemo(
    () =>
      COLUMNS_CONSTANTS.map((col) =>
        col.key === "region"
          ? {
            ...col,
            label: intl.formatMessage({ id: "candidate.region", defaultMessage: "Region" }),
          }
          : col,
      ),
    [intl],
  );

  const handleUpdateWhitelisted = (nextWhitelisted) => {
    if (!id) return;
    if (!candidate) return;

    setSavingWhitelisted(true);
    setError(null);

    sh.put(`/internal/candidates/${id}`, {
      candidate: {
        whitelisted: nextWhitelisted,
      },
      authenticity_token,
    })
      .then((res) => {
        setCandidate(res.data);
        enqueueSnackbar(`Candidate ${nextWhitelisted ? "whitelisted" : "blacklisted"}`, { variant: "success" });
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to update blacklist status");
      })
      .finally(() => {
        setSavingWhitelisted(false);
      });
  };

  const handleDeleteCandidate = () => {
    if (!id) return;
    if (!candidate) return;

    setLoading(true);
    setError(null);

    sh.delete(`/internal/candidates/${id}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        candidate: candidate,
        authenticity_token
      },
    })
      .then(() => {
        goBack();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to delete candidate");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleRestoreCandidate = () => {
    if (!id) return;
    if (!candidate) return;

    setRestoring(true);
    setError(null);

    sh.post(`/internal/candidates/${id}/restore`, { authenticity_token })
      .then((res) => {
        setCandidate(res.data);
        enqueueSnackbar("Candidate restored", { variant: "success" });
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to restore candidate");
      })
      .finally(() => {
        setRestoring(false);
      });
  };

  const editDefaultValues = useMemo(() => {
    if (!candidate) return {};

    const toComma = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
    const rawUtmSource = candidate.utm_source || "";
    const knownSource = KNOWN_SOURCES.includes(rawUtmSource);
    // recruiter_summaries is a per-locale cache; the form only edits one language at a time, so show
    // the candidate's own locale when it has an entry, otherwise whichever one exists.
    const recruiterSummaries = candidate.recruiter_summaries || {};
    const aiSummaryLocale = recruiterSummaries[candidate.preferred_locale]
      ? candidate.preferred_locale
      : Object.keys(recruiterSummaries)[0] || "";

    return {
      first_name: candidate.first_name || "",
      last_name: candidate.last_name || "",
      email: candidate.email || "",
      phone: candidate.phone || "",
      region: candidate.region || "",
      country: candidate.country || "",
      zipcode: candidate.zipcode || "",
      city: candidate.city || "",
      job_title: candidate.job_title || "",
      desired_sector: candidate.desired_sector || [],
      subsectors: candidate.subsectors || [],
      desired_job_title: candidate.desired_job_title || "",
      desired_locations: toComma((candidate.desired_locations || []).map((l) => (typeof l === "string" ? l : l?.label))),
      portfolio_url: candidate.portfolio_url || "",
      experience_year: candidate.experience_year || "",
      preferred_locale: candidate.preferred_locale || "",
      availability: candidate.availability || "",
      candidate_status:
        candidate.candidate_status === "independent_type"
          ? "independent"
          : candidate.candidate_status || "",
      highest_degree: candidate.highest_degree || "",
      presentation: candidate.presentation || "",
      ai_summary: aiSummaryLocale ? recruiterSummaries[aiSummaryLocale] : "",
      ai_summary_locale: aiSummaryLocale,
      recruiter_summaries: recruiterSummaries,
      whitelisted: candidate.whitelisted !== undefined ? Boolean(candidate.whitelisted) : true,
      work_permit: candidate.work_permit !== undefined ? Boolean(candidate.work_permit) : true,
      source: knownSource ? rawUtmSource : (rawUtmSource ? "other" : ""),
      source_other: !knownSource && rawUtmSource ? rawUtmSource : "",
      // Served by Internal::CandidatesController#show only — the index payload leaves them out.
      experiences: candidate.experiences || [],
      trainings: candidate.trainings || [],
      skills: candidate.skills || [],
      certificates: candidate.certificates || [],
    };
  }, [candidate]);

  const handleEditCandidate = (values, { resumeFile, close } = {}) => {
    // values.source is already resolved by the modal (source_other folded in)
    // remap to utm_source since the update action permits utm_source, not source
    const editPayload = {
      ...values,
      utm_source: values.source || undefined,
      source: undefined,
    };

    if (!resumeFile) {
      return sh.put(`/internal/candidates/${id}`, {
        candidate: editPayload,
        authenticity_token,
      })
        .then((res) => {
          setCandidate(res.data);
          enqueueSnackbar("Candidate updated", { variant: "success" });
          close?.();
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            window.location.href = "/internal/sign_in";
            return;
          }
          const messages = err?.response?.data?.errors;
          if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
            messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
            return;
          }
          enqueueSnackbar("Failed to update candidate", { variant: "error" });
        });
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);

    const appendValue = (key, value) => appendFormValue(formData, key, value);

    Object.entries(editPayload).forEach(([k, v]) => appendValue(`candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);

    return sh.put(`/internal/candidates/${id}`, formData)
      .then((res) => {
        setCandidate(res.data);
        enqueueSnackbar("Candidate updated", { variant: "success" });
        close?.();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        const messages = err?.response?.data?.errors;
        if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
          messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
          return;
        }
        enqueueSnackbar("Failed to update candidate", { variant: "error" });
      });
  };

  const handleSetPassword = (password) => {
    if (!id) return;
    setSavingPassword(true);

    sh.post(`/internal/candidates/${id}/set_password`, {
      password,
      authenticity_token,
    })
      .then((res) => {
        setCandidate(res.data);
        enqueueSnackbar("Password updated", { variant: "success" });
        setPasswordDialogOpen(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        const msg = err?.response?.data?.error || "Failed to update password";
        enqueueSnackbar(msg, { variant: "error" });
      })
      .finally(() => setSavingPassword(false));
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 72px)",
        backgroundColor: "hsl(140 10% 95%)",
      }}
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteCandidate}
        title="Archive candidate?"
        description="This candidate will be archived and won't be able to login anymore."
        confirmLabel="Archive"
        destructive
      />
      <ConfirmDialog
        open={showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(false)}
        onConfirm={() => {
          setShowRestoreConfirm(false);
          handleRestoreCandidate();
        }}
        title="Restore candidate?"
        description="This candidate will be restored and visible again in the active lists."
        confirmLabel="Restore"
      />
      <ConfirmDialog
        open={whitelistConfirmValue !== null}
        onClose={() => setWhitelistConfirmValue(null)}
        onConfirm={() => {
          handleUpdateWhitelisted(whitelistConfirmValue);
          setWhitelistConfirmValue(null);
        }}
        title={whitelistConfirmValue ? "Whitelist candidate" : "Blacklist candidate"}
        description={
          whitelistConfirmValue
            ? "This candidate will be whitelisted."
            : "This candidate will be blacklisted."
        }
        confirmLabel={whitelistConfirmValue ? "Whitelist" : "Blacklist"}
      />
      <AddCandidateToPipelineFlow
        open={addToPipelineOpen}
        onClose={() => setAddToPipelineOpen(false)}
        onAdded={(data, pipeline) => {
          const wasSkipped = !data?.wasCreated && !data?.created_membership;
          const message = wasSkipped
            ? `Skipped - already in pipeline${pipeline?.title ? ` “${pipeline.title}”` : "””"}`
            : `Added to pipeline${pipeline?.title ? ` “${pipeline.title}”` : "””"}`;
          enqueueSnackbar(message, { variant: wasSkipped ? "info" : "success" });
        }}
        candidate={candidate}
        authenticityToken={authenticity_token}
      />
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSubmit={handleSetPassword}
        submitting={savingPassword}
        title="Change candidate password"
        description="Sets a new password directly. The candidate will be marked as having a generated password."
      />
      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minWidth: 0 }}
            >
              {/* Back */}
              <Button
                onClick={goBack}
                disableRipple
                sx={{
                  minWidth: 0,
                  p: 0.75,
                  color: "hsl(150 10% 46%)",
                  "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
                }}
              >
                <ArrowLeft size={20} />
              </Button>

              {/* Avatar + Info */}
              {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={140} height={20} />
                    <Skeleton width={200} height={14} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      backgroundColor: "hsl(150 38% 17%)",
                      color: "hsl(80 97% 72%)",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 16, sm: 18 },
                          fontWeight: 600,
                          color: "hsl(150 30% 12%)",
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {title}
                      </Typography>
                      {candidate?.availability && (
                        <Chip
                          label={candidate.availability.replace("_", " ")}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 500,
                            textTransform: "capitalize",
                            backgroundColor: "hsla(150, 38%, 17%, 0.1)",
                            color: "hsl(150 38% 17%)",
                          }}
                        />
                      )}
                      {candidate?.candidate_status && (
                        <Chip
                          label={candidate.candidate_status.replace("_", " ")}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            fontWeight: 500,
                            textTransform: "capitalize",
                            backgroundColor: "hsl(140 10% 92%)",
                            color: "hsl(150 10% 46%)",
                          }}
                        />
                      )}
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{
                        mt: 0.25,
                        flexWrap: "wrap",
                        rowGap: 0.25,
                      }}
                    >
                      {candidate?.email && (
                        <Stack direction="row" alignItems="center" spacing={0}>
                          <Typography
                            component="a"
                            href={`mailto:${candidate.email}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontSize: 12,
                              color: "hsl(150 10% 46%)",
                              textDecoration: "none",
                              "&:hover": { color: "hsl(150 30% 12%)" },
                            }}
                          >
                            <Mail size={13} />
                            {candidate.email}
                          </Typography>
                          <CopyButton value={candidate.email} />
                        </Stack>
                      )}
                      {candidate?.phone && (
                        <Stack direction="row" alignItems="center" spacing={0}>
                          <Typography
                            component="a"
                            href={`tel:${candidate.phone}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontSize: 12,
                              color: "hsl(150 10% 46%)",
                              textDecoration: "none",
                              "&:hover": { color: "hsl(150 30% 12%)" },
                            }}
                          >
                            <Phone size={13} />
                            {candidate.phone}
                          </Typography>
                          <CopyButton value={candidate.phone} />
                        </Stack>
                      )}
                      {(candidate?.city || candidate?.country) && (
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                          }}
                        >
                          <MapPin size={13} />
                          {[candidate.city, candidate.country].filter(Boolean).join(", ")}
                        </Typography>
                      )}
                      {candidate?.godview_url && (
                        <a
                          href={candidate.godview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#079344", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textDecoration: "none", fontSize: 12 }}
                          onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          Godview ↗
                        </a>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}

              {/* Actions */}
              <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                {candidate?.id && (
                  <FollowButton
                    followableType="Candidate"
                    followableId={candidate.id}
                    followed={candidate.followed}
                    onChange={(next) => setCandidate((prev) => (prev ? { ...prev, followed: next } : prev))}
                  />
                )}
                <Button
                  endIcon={<MoreVertical size={18} />}
                  onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                  disabled={loading || !candidate?.id || savingWhitelisted || restoring}
                  disableRipple
                  size="small"
                  sx={{
                    color: "hsl(150 30% 12%)",
                    fontWeight: 600,
                    fontSize: 13,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    textTransform: "none",
                    borderRadius: "0.5rem",
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "#fff",
                    border: "1px solid hsl(140 8% 88%)",
                    transition: "opacity 0.15s ease",
                    "&:hover": {
                      backgroundColor: "#fff",
                      opacity: 0.8,
                    },
                  }}
                >
                  Actions
                </Button>

                <Menu
                  disableScrollLock
                  anchorEl={actionsAnchorEl}
                  open={actionsOpen}
                  onClose={() => setActionsAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  {isArchived ? (
                    <MenuItem
                      onClick={() => {
                        setActionsAnchorEl(null);
                        setShowRestoreConfirm(true);
                      }}
                    >
                      Restore candidate
                    </MenuItem>
                  ) : (
                    [
                      <MenuItem
                        key="edit"
                        onClick={() => {
                          setActionsAnchorEl(null);
                          openEditCandidate({
                            onSubmit: handleEditCandidate,
                            defaultValues: editDefaultValues,
                            existingResumeUrl: candidate?.resume_url,
                            candidateId: id,
                          });
                        }}
                      >
                        Edit candidate
                      </MenuItem>,
                      <MenuItem
                        key="add-to-pipeline"
                        onClick={() => {
                          setActionsAnchorEl(null);
                          setAddToPipelineOpen(true);
                        }}
                      >
                        Add to pipeline
                      </MenuItem>,
                      (() => {
                        const item = (
                          <MenuItem
                            key="branded-cv"
                            disabled={!hasCv}
                            onClick={() => {
                              if (!hasCv) return;
                              setActionsAnchorEl(null);
                              generateBrandedCv();
                            }}
                          >
                            {branded?.status === "ready" || branded?.status === "failed" ? "Regenerate Squarehub CV" : "Generate Squarehub CV"}
                          </MenuItem>
                        );
                        return hasCv ? item : (
                          <Tooltip key="branded-cv" title="This candidate has no CV on file yet">
                            <span>{item}</span>
                          </Tooltip>
                        );
                      })(),
                      <MenuItem
                        key="change-password"
                        onClick={() => {
                          setActionsAnchorEl(null);
                          setPasswordDialogOpen(true);
                        }}
                      >
                        Change password
                      </MenuItem>,
                      <MenuItem
                        key="whitelist"
                        onClick={() => {
                          setActionsAnchorEl(null);
                          setWhitelistConfirmValue(isBlacklisted);
                        }}
                      >
                        {isBlacklisted ? "Whitelist candidate" : "Blacklist candidate"}
                      </MenuItem>,
                      <MenuItem
                        key="delete"
                        onClick={() => {
                          setActionsAnchorEl(null);
                          setShowDeleteConfirm(true);
                        }}
                        sx={{ color: (theme) => theme.palette.error.main }}
                      >
                        Archive candidate
                      </MenuItem>,
                    ]
                  )}
                </Menu>
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Page Content */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Grid container spacing={3}>
          {/* Main Content */}
          <Grid item xs={12} lg={8}>

            {/* Tabs */}
            <Box sx={{ borderBottom: "1px solid hsl(140 8% 88%)", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    minHeight: 36,
                    py: 0.5,
                    px: 1.5,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(150 10% 46%)",
                    "&.Mui-selected": { color: "hsl(150 38% 17%)" },
                  },
                  "& .MuiTab-iconWrapper": { fontSize: 16 },
                  "& .MuiTabs-indicator": { backgroundColor: "hsl(150 38% 17%)" },
                }}
              >
                <Tab icon={<User size={16} />} iconPosition="start" label="Details" />
                <Tab
                  icon={<Briefcase size={16} />}
                  iconPosition="start"
                  label={`Job Applications${jobAppCount ? ` (${jobAppCount})` : ""}`}
                />
                <Tab icon={<FileText size={16} />} iconPosition="start" label="CV" />
                <Tab icon={<MousePointerClick size={16} />} iconPosition="start" label="Activity" />
              </Tabs>
            </Box>

            {/* Tab: Details */}
            {activeTab === 0 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                  border: "1px solid hsl(140 8% 88%)",
                  p: 4,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "hsl(150 30% 12%)",
                    fontWeight: 600,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <User size={20} color="hsl(150 38% 17%)" />
                  Candidate Details
                </Typography>

                {error && (
                  <Box
                    sx={{
                      p: 2,
                      mb: 3,
                      backgroundColor: "hsla(0, 72%, 51%, 0.08)",
                      border: "1px solid hsla(0, 72%, 51%, 0.2)",
                      borderRadius: "calc(0.75rem - 2px)",
                    }}
                  >
                    <Typography sx={{ color: "hsl(0 72% 51%)", fontSize: 14 }}>{error}</Typography>
                  </Box>
                )}

                {loading ? (
                  <Grid container spacing={3}>
                    {[...Array(8)].map((_, idx) => (
                      <Grid item xs={12} sm={6} key={idx}>
                        <Skeleton variant="text" width="40%" />
                        <Skeleton variant="text" width="60%" />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  candidate && (
                    <Stack spacing={4}>
                      {[...DETAIL_SECTIONS, { title: "Other", icon: Globe, keys: UNGROUPED_KEYS }].map((section) => {
                        const tiles = section.keys
                          .map((key) => ({ key, col: detailColumns.find((c) => c.key === key) }))
                          .filter(({ col }) => col)
                          .map(({ key, col }) => ({ key, label: col.label, value: formatCandidateCell(candidate, key) }))
                          .filter(({ value }) => value || value === 0);

                        if (tiles.length === 0) return null;

                        return (
                          <Box key={section.title}>
                            <SectionTitle icon={section.icon}>{section.title}</SectionTitle>
                            <Grid container spacing={2} alignItems="stretch">
                              {tiles.map(({ key, label, value }) => (
                                <Grid item xs={12} sm={key === "presentation" || key === "recruiter_summaries" ? 12 : 6} key={key}>
                                  <DetailTile label={label}>
                                    {value}
                                    {(key === "email" || key === "phone") && <CopyButton value={candidate[key]} />}
                                  </DetailTile>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        );
                      })}

                      <Box>
                        <SectionTitle icon={Briefcase} count={(candidate.experiences || []).length}>
                          Experience
                        </SectionTitle>
                        {(candidate.experiences || []).length === 0 ? (
                          <EmptySection>No experience recorded yet.</EmptySection>
                        ) : (
                          <Stack spacing={1.5}>
                            {candidate.experiences.map((xp) => (
                              <RecordCard
                                key={xp.id}
                                title={xp.title}
                                subtitle={[xp.company, xp.location].filter(Boolean).join(" · ")}
                                meta={formatDateRange(xp.start_date, xp.end_date)}
                                chip={xp.job_status ? humanizeRegion(xp.job_status) : null}
                              >
                                {xp.description && (
                                  <Typography
                                    sx={{
                                      mt: 1,
                                      fontSize: 13,
                                      color: "hsl(150 30% 12%)",
                                      whiteSpace: "pre-line",
                                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                                    }}
                                  >
                                    {xp.description}
                                  </Typography>
                                )}
                              </RecordCard>
                            ))}
                          </Stack>
                        )}
                      </Box>

                      <Box>
                        <SectionTitle icon={GraduationCap} count={(candidate.trainings || []).length}>
                          Education
                        </SectionTitle>
                        {(candidate.trainings || []).length === 0 ? (
                          <EmptySection>No education recorded yet.</EmptySection>
                        ) : (
                          <Stack spacing={1.5}>
                            {candidate.trainings.map((tr) => (
                              <RecordCard
                                key={tr.id}
                                title={[tr.diploma, tr.study_field].filter(Boolean).join(" — ")}
                                subtitle={[tr.institution, tr.location].filter(Boolean).join(" · ")}
                                meta={formatDateRange(tr.start_date, tr.end_date)}
                                chip={tr.honors || null}
                              />
                            ))}
                          </Stack>
                        )}
                      </Box>

                      <Box>
                        <SectionTitle icon={Sparkles} count={(candidate.skills || []).length}>
                          Skills
                        </SectionTitle>
                        {(candidate.skills || []).length === 0 ? (
                          <EmptySection>No skills recorded yet.</EmptySection>
                        ) : (
                          <Stack spacing={2}>
                            {Object.entries(SKILL_CATEGORY_LABELS).map(([category, label]) => {
                              const group = (candidate.skills || []).filter((s) => s.category === category);
                              if (group.length === 0) return null;

                              return (
                                <Box key={category}>
                                  <Typography
                                    sx={{
                                      fontSize: 11,
                                      textTransform: "uppercase",
                                      letterSpacing: 0.6,
                                      color: "hsl(150 10% 46%)",
                                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                                      mb: 0.75,
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    {group.map((skill) => (
                                      <Chip
                                        key={`${skill.category}-${skill.name}`}
                                        label={skillLabel(skill)}
                                        size="small"
                                        sx={{
                                          fontSize: 12,
                                          backgroundColor: "hsl(140 10% 95%)",
                                          border: "1px solid hsl(140 8% 88%)",
                                          color: "hsl(150 30% 12%)",
                                          fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                  )
                )}
              </Paper>
            )}

            {/* Tab: Job Applications */}
            {activeTab === 1 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                  border: "1px solid hsl(140 8% 88%)",
                  p: 4,
                }}
              >
                <CandidateJobApplicationsPanel candidateId={id} candidateResumeUrl={candidate?.resume_url} onApplied={() => { setTimelineRefreshKey((k) => k + 1); fetchCandidate(); }} onCountChange={setJobAppCount} />
              </Paper>
            )}

            {/* Tab: CV */}
            {activeTab === 2 && candidate && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                  border: "1px solid hsl(140 8% 88%)",
                  p: 4,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "hsl(150 30% 12%)",
                    fontWeight: 600,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    mb: 3,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <FileText size={20} color="hsl(150 38% 17%)" />
                  CV
                </Typography>

                <Stack spacing={3}>
                  {hasCv && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, borderRadius: "8px", border: "1px solid hsl(140 8% 88%)", height: "100%" }}>
                          <CvParsePanel receipt={candidate.cv_parse} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ p: 2, borderRadius: "8px", border: "1px solid hsl(140 8% 88%)", height: "100%" }}>
                          <BrandedCvSection candidateId={id} hasCv={hasCv} branded={branded} onGenerate={generateBrandedCv} />
                        </Box>
                      </Grid>
                    </Grid>
                  )}

                  {hasCv ? (
                    (() => {
                      const brandedReady = branded?.status === "ready";
                      const officialIsBranded = Boolean(candidate.cv_vault_item_id);
                      const originalUrl = officialIsBranded ? `/internal/cv_vault_items/${candidate.cv_vault_item_id}/file` : null;
                      const originalIsDocx = /\.docx$/i.test(candidate.cv_vault_item_filename || "");
                      const officialLabel = officialIsBranded ? "Squarehub CV" : "Document";
                      const alternateLabel = officialIsBranded ? "Original CV" : "Squarehub CV";
                      const hasAlternate = officialIsBranded || brandedReady;
                      const docTab = cvDocTab === "alternate" && !hasAlternate ? "official" : cvDocTab;
                      const showingBranded = officialIsBranded ? docTab === "official" : docTab === "alternate";
                      const showingOriginal = officialIsBranded && docTab === "alternate";
                      return (
                        <Box
                          sx={{
                            height: 760,
                            border: "1px solid hsl(140 8% 88%)",
                            borderRadius: "0.75rem",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              px: 1.5,
                              py: 1,
                              borderBottom: "1px solid hsl(140 8% 88%)",
                              backgroundColor: "hsl(140 10% 95%)",
                            }}
                          >
                            <CvDocTabs
                              value={docTab}
                              onChange={setCvDocTab}
                              officialLabel={officialLabel}
                              hasOfficial={Boolean(candidate.resume_url)}
                              hasText={Boolean(candidate.cv_text)}
                              alternateLabel={alternateLabel}
                              hasAlternate={hasAlternate}
                            />
                            {showingBranded && (
                              <Button
                                size="small"
                                component="a"
                                href={`/internal/candidates/${id}/branded_cv/download`}
                                download={branded.filename}
                                startIcon={<Download size={15} />}
                                sx={{ textTransform: "none", fontSize: 12, minHeight: 0, py: 0.25, borderRadius: "8px", color: "hsl(150 38% 17%)" }}
                              >
                                Download
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ flex: 1, minHeight: 0 }}>
                            {showingBranded ? (
                              <Box
                                key={branded.updated_at}
                                component="embed"
                                src={`/internal/candidates/${id}/branded_cv/download`}
                                type="application/pdf"
                                sx={{ width: "100%", height: "100%", border: "none" }}
                              />
                            ) : showingOriginal ? (
                              <CvViewer
                                fileUrl={originalUrl}
                                isPdf={!originalIsDocx}
                                isDocx={originalIsDocx}
                                cvText={null}
                                filename={candidate.cv_vault_item_filename || "Original CV"}
                                tab="document"
                                onTabChange={setCvDocTab}
                                hideHeader
                              />
                            ) : (
                              <CvViewer
                                fileUrl={candidate.resume_url}
                                isPdf={Boolean(candidate.resume_url)}
                                cvText={candidate.cv_text}
                                filename={candidate.resume_url ? "CV on file" : "No file"}
                                tab={docTab === "text" ? "text" : "document"}
                                onTabChange={setCvDocTab}
                                hideHeader
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })()
                  ) : (
                    <EmptySection>No CV uploaded for this candidate yet.</EmptySection>
                  )}
                </Stack>
              </Paper>
            )}

            {activeTab === 3 && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: "0.75rem",
                  overflow: "hidden",
                  border: "1px solid hsl(140 8% 88%)",
                  p: 4,
                  pt: 1,
                }}
              >
                <FeatureActivityPanel scope="candidate" candidateId={id} />
              </Paper>
            )}
          </Grid>

          {/* Sidebar - Notes */}
          <Grid item xs={12} lg={4}>
            <Box
              sx={{
                position: { lg: "sticky" },
                top: { lg: 80 },
              }}
            >
              <CandidateTimelinePanel candidateId={id} refreshTrigger={timelineRefreshKey} />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default CandidateShowPage;